import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { EditorContent, ReactRenderer, useEditor, generateHTML } from '@tiptap/react';
import type { Editor, JSONContent } from '@tiptap/core';
import type { SuggestionProps } from '@tiptap/suggestion';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import ImageResize from 'tiptap-extension-resize-image';
import Mention from '@tiptap/extension-mention';
import TurndownService from 'turndown';
import { marked } from 'marked';
import { Tooltip } from './Tooltip';
import { ensureRichText } from '@/shared/lib/richText';

type EditorMode = 'wysiwyg' | 'markdown';

type MentionOption = {
  id: number;
  label: string;
};

type UploadMode = 'file' | 'media' | 'screenshot';

/** Minimal shape of the chained `setImage` command contributed by the image
 *  extension, which is not surfaced on TipTap's base ChainedCommands type. */
type SetImageChain = { setImage: (attrs: { src: string; alt: string }) => { run: () => boolean } };

/** Insert a block image at the current (or given) position without leaking
 *  `any` at every call site. */
const insertImage = (editor: Editor, src: string, alt: string, focusPos?: number): void => {
  const chain = focusPos != null ? editor.chain().focus(focusPos) : editor.chain().focus();
  (chain as unknown as SetImageChain).setImage({ src, alt }).run();
};

export type UploadResult = {
  url: string;
  name: string;
  contentType?: string | null;
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  mentionOptions?: MentionOption[];
  currentUserId?: number;
  creatorId?: number;
  assigneeId?: number | null;
  watcherIds?: number[];
  onUpload?: (files: File[], mode: UploadMode) => Promise<UploadResult[]>;
  /** Buffer picked files locally when there is no `onUpload` (e.g. the create form has no
   *  card id yet). Called with the chosen files so the parent can upload them after save. */
  onLocalFiles?: (files: File[]) => void;
  allowLocalPaste?: boolean;
  /**
   * Whether the current content differs from the last-saved value. Gates the
   * undo/redo buttons so they aren't active on a freshly-opened, unedited card
   * (BUGS-7). Defaults to true for editors with no saved baseline (e.g. the
   * create-card dialog), preserving their behaviour.
   */
  dirty?: boolean;
  /** When true, the formatting toolbar starts hidden and is exposed via a toggle in the mode bar. */
  defaultToolbarHidden?: boolean;
  /** Optional click-on-image hook. When provided, clicking an image (without dragging) calls this with its src. The image-resize selection on a separate click still works. */
  onImageClick?: (src: string) => void;
};

export type TaskRichTextEditorHandle = {
  insertImage: (url: string, alt?: string) => void;
  insertLink: (url: string, text?: string) => void;
  focus: () => void;
  /** Upload any base64-embedded images in the current content and replace with server URLs. Call before save/send. */
  flushEmbeddedImages: () => Promise<string>;
};

const turndown = new TurndownService({ headingStyle: 'atx', codeBlockStyle: 'fenced' });

// Keep data-URL images as raw HTML so they survive the markdown round-trip
turndown.addRule('dataUrlImage', {
  filter: (node: HTMLElement) =>
    node.nodeName === 'IMG' && (node.getAttribute('src') ?? '').startsWith('data:'),
  replacement: (_content: string, node: HTMLElement) => {
    const src = node.getAttribute('src') ?? '';
    const alt = node.getAttribute('alt') ?? '';
    return `\n<img src="${src}" alt="${alt}" />\n`;
  },
});

// Strip <p> wrappers inside <li> to produce tight lists and prevent spacing bloat
turndown.addRule('listItemParagraph', {
  filter: (node: HTMLElement) =>
    node.nodeName === 'P' &&
    node.parentNode != null &&
    (node.parentNode as HTMLElement).nodeName === 'LI',
  replacement: (content: string) => content,
});

const tiptapExtensionsForHtml = [
  StarterKit.configure({ heading: false }),
  Underline,
  ImageResize,
  Link.configure({ openOnClick: true, autolink: true, defaultProtocol: 'https' }),
  Mention.configure({
    HTMLAttributes: { class: 'mention' },
    renderText: ({ node }) => `@${node.attrs.label ?? 'участник'}`,
  }),
];

const jsonToMarkdown = (json: string): string => {
  try {
    const parsed = JSON.parse(json);
    const html = generateHTML(parsed, tiptapExtensionsForHtml);
    return turndown.turndown(html);
  } catch {
    return '';
  }
};

const markdownToJson = (md: string): string => {
  try {
    const html = marked.parse(md, { async: false }) as string;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return JSON.stringify({ type: 'doc', content: htmlToTiptapContent(tempDiv) });
  } catch {
    return JSON.stringify({ type: 'doc', content: [{ type: 'paragraph' }] });
  }
};

const isBlockNode = (n: JSONContent) => n.type === 'image' || n.type === 'codeBlock' || n.type === 'bulletList' || n.type === 'orderedList';

const htmlToTiptapContent = (el: HTMLElement): JSONContent[] => {
  const result: JSONContent[] = [];
  el.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? '';
      if (text) result.push({ type: 'text', text });
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const htmlEl = node as HTMLElement;
    const tag = htmlEl.tagName.toLowerCase();
    if (tag === 'p') {
      const children = htmlToTiptapContent(htmlEl);
      // Images are block-level in Tiptap — extract them from paragraphs
      const inline: JSONContent[] = [];
      children.forEach((child) => {
        if (isBlockNode(child)) {
          if (inline.length) { result.push({ type: 'paragraph', content: [...inline] }); inline.length = 0; }
          result.push(child);
        } else {
          inline.push(child);
        }
      });
      if (inline.length) result.push({ type: 'paragraph', content: inline });
      else if (!children.length) result.push({ type: 'paragraph' });
    } else if (tag === 'strong' || tag === 'b') {
      const inner = htmlToTiptapContent(htmlEl);
      inner.forEach((n) => { if (n.type === 'text') n.marks = [...(n.marks ?? []), { type: 'bold' }]; });
      result.push(...inner);
    } else if (tag === 'em' || tag === 'i') {
      const inner = htmlToTiptapContent(htmlEl);
      inner.forEach((n) => { if (n.type === 'text') n.marks = [...(n.marks ?? []), { type: 'italic' }]; });
      result.push(...inner);
    } else if (tag === 'del' || tag === 's') {
      const inner = htmlToTiptapContent(htmlEl);
      inner.forEach((n) => { if (n.type === 'text') n.marks = [...(n.marks ?? []), { type: 'strike' }]; });
      result.push(...inner);
    } else if (tag === 'u') {
      const inner = htmlToTiptapContent(htmlEl);
      inner.forEach((n) => { if (n.type === 'text') n.marks = [...(n.marks ?? []), { type: 'underline' }]; });
      result.push(...inner);
    } else if (tag === 'a') {
      const inner = htmlToTiptapContent(htmlEl);
      inner.forEach((n) => { if (n.type === 'text') n.marks = [...(n.marks ?? []), { type: 'link', attrs: { href: htmlEl.getAttribute('href') ?? '' } }]; });
      result.push(...inner);
    } else if (tag === 'ul') {
      result.push({ type: 'bulletList', content: Array.from(htmlEl.children).filter((c) => c.tagName.toLowerCase() === 'li').map((li) => parseLi(li as HTMLElement)) });
    } else if (tag === 'ol') {
      result.push({ type: 'orderedList', content: Array.from(htmlEl.children).filter((c) => c.tagName.toLowerCase() === 'li').map((li) => parseLi(li as HTMLElement)) });
    } else if (tag === 'li') {
      // Handled by parseLi from parent list
      result.push(...htmlToTiptapContent(htmlEl));
    } else if (tag === 'pre') {
      const code = htmlEl.querySelector('code');
      result.push({ type: 'codeBlock', content: [{ type: 'text', text: code?.textContent ?? htmlEl.textContent ?? '' }] });
    } else if (tag === 'code') {
      result.push({ type: 'text', text: htmlEl.textContent ?? '', marks: [{ type: 'code' }] });
    } else if (tag === 'img') {
      result.push({ type: 'image', attrs: { src: htmlEl.getAttribute('src') ?? '', alt: htmlEl.getAttribute('alt') ?? '' } });
    } else if (tag === 'br') {
      result.push({ type: 'hardBreak' });
    } else {
      result.push(...htmlToTiptapContent(htmlEl));
    }
  });
  return result;
};

const parseLi = (li: HTMLElement): JSONContent => {
  const children = htmlToTiptapContent(li);
  // If children already contain block-level nodes (paragraphs, lists), use them directly
  const hasBlock = children.some((c) => c.type === 'paragraph' || c.type === 'bulletList' || c.type === 'orderedList');
  if (hasBlock) {
    return { type: 'listItem', content: children };
  }
  // Otherwise wrap inline content in a paragraph
  return { type: 'listItem', content: [{ type: 'paragraph', content: children.length ? children : undefined }] };
};

const Root = styled.div`
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  background: var(--bg-primary);
  overflow: hidden;
`;

const ModeBar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  border-bottom: 1px solid var(--border-subtle);
  background: var(--bg-surface);
  flex-wrap: nowrap;
  overflow-x: auto;
`;

const ModeBtn = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  border: none;
  background: ${({ $active }) => ($active ? 'var(--accent-muted)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--accent)' : 'var(--text-tertiary)')};
  font: inherit;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.1s ease;

  &:hover {
    color: var(--text-primary);
    background: var(--bg-hover);
  }
`;

const MdTextarea = styled.textarea`
  width: 100%;
  min-height: 200px;
  padding: 14px 16px;
  border: none;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-family: 'Fira Code', 'Cascadia Code', 'JetBrains Mono', monospace;
  font-size: 13px;
  line-height: 1.6;
  resize: vertical;
  outline: none;
  white-space: pre-wrap;

  &::placeholder {
    color: var(--text-tertiary);
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  padding: 8px 10px;
  border-bottom: 1px solid var(--border-subtle);
  background: var(--bg-surface);
`;

const ToolbarBtn = styled.button<{ $active?: boolean }>`
  min-width: 28px;
  height: 28px;
  padding: 0 6px;
  border-radius: 6px;
  border: 1px solid ${({ $active }) => ($active ? 'var(--accent)' : 'transparent')};
  background: ${({ $active }) => ($active ? 'var(--accent-muted)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--text-primary)' : 'var(--text-secondary)')};
  font: inherit;
  font-size: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;

  &:hover:not(:disabled) {
    background: var(--bg-hover);
    color: var(--text-primary);
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }

  svg { width: 15px; height: 15px; }
`;

const Divider = styled.div`
  width: 1px;
  height: 20px;
  background: var(--border-subtle);
  margin: 0 2px;
`;

const LinkRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 8px 10px;
  border-bottom: 1px solid var(--border-subtle);
  background: color-mix(in srgb, var(--bg-surface) 80%, transparent);
`;

const LinkInput = styled.input`
  min-width: 0;
  flex: 1;
  height: 34px;
  border-radius: 8px;
  border: 1px solid var(--border-subtle);
  background: var(--bg-primary);
  color: var(--text-primary);
  padding: 0 10px;
  font: inherit;

  &:focus {
    outline: none;
    border-color: var(--accent);
  }
`;

const LinkAction = styled.button`
  height: 34px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid var(--accent);
  background: var(--accent);
  color: var(--accent-contrast);
  font: inherit;
  font-size: 12px;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

const EditorWrap = styled.div<{ $dragOver?: boolean }>`
  position: relative;

  ${({ $dragOver }) => $dragOver && `
    &::after {
      content: 'Перетащите файлы сюда';
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(124, 140, 255, 0.06);
      border: 2px dashed var(--accent);
      border-radius: 0 0 9px 9px;
      color: var(--accent);
      font-size: 13px;
      font-weight: 600;
      z-index: 5;
      pointer-events: none;
    }
  `}

  .ProseMirror {
    min-height: 150px;
    padding: 14px 16px;
    color: var(--text-primary);
    line-height: 1.6;
    font-size: 14px;
    outline: none;
    white-space: pre-wrap;
  }

  .ProseMirror p.is-editor-empty:first-of-type::before {
    content: attr(data-placeholder);
    color: var(--text-tertiary);
    float: left;
    height: 0;
    pointer-events: none;
  }

  .ProseMirror p,
  .ProseMirror ul,
  .ProseMirror ol,
  .ProseMirror pre,
  .ProseMirror blockquote {
    margin: 0 0 10px;
  }

  .ProseMirror ul,
  .ProseMirror ol {
    padding-left: 20px;
  }

  .ProseMirror li > p {
    margin-bottom: 2px;
  }
  .ProseMirror li > p:last-child {
    margin-bottom: 0;
  }

  .ProseMirror pre {
    padding: 10px 12px;
    border-radius: 8px;
    background: #111827;
    color: #e5e7eb;
    overflow-x: auto;
  }

  .ProseMirror code {
    padding: 1px 4px;
    border-radius: 4px;
    background: color-mix(in srgb, var(--bg-hover) 85%, transparent);
  }

  .ProseMirror pre code {
    padding: 0;
    background: transparent;
  }

  .ProseMirror a {
    color: var(--accent);
    text-decoration: underline;
    cursor: pointer;
  }

  .ProseMirror img {
    max-width: 100%;
    border-radius: 10px;
    display: block;
    margin: 8px 0;
  }

  .ProseMirror img.ProseMirror-selectednode {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }

  .mention {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 1px 6px;
    border-radius: 999px;
    background: var(--accent-muted);
    color: var(--accent);
    font-weight: 600;
  }
`;

const suggestionRootStyle = `
  position: fixed;
  z-index: 1200;
  min-width: 220px;
  max-width: 280px;
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  background: var(--bg-surface);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.24);
  padding: 6px;
`;

const suggestionItemBase = {
  padding: '8px 10px',
  borderRadius: 8,
  fontSize: 13,
  color: 'var(--text-primary)',
  cursor: 'pointer',
} as const;

const suggestionItemActive = {
  background: 'var(--accent-muted)',
  color: 'var(--accent)',
} as const;

const MentionList = forwardRef<
  { onKeyDown: (event: KeyboardEvent) => boolean },
  { items: MentionOption[]; command: (item: MentionOption) => void }
>(({ items, command }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: (event: KeyboardEvent) => {
      if (!items.length) return false;
      if (event.key === 'ArrowUp') {
        setSelectedIndex((current) => (current + items.length - 1) % items.length);
        return true;
      }
      if (event.key === 'ArrowDown') {
        setSelectedIndex((current) => (current + 1) % items.length);
        return true;
      }
      if (event.key === 'Enter') {
        const item = items[selectedIndex];
        if (item) command(item);
        return true;
      }
      return false;
    },
  }));

  if (!items.length) {
    return <div style={{ padding: '8px 10px', fontSize: 13, color: 'var(--text-tertiary)' }}>Никого не найдено</div>;
  }

  return (
    <div>
      {items.map((item, index) => (
        <div
          key={item.id}
          style={index === selectedIndex ? { ...suggestionItemBase, ...suggestionItemActive } : suggestionItemBase}
          onMouseDown={(event) => {
            event.preventDefault();
            command(item);
          }}
        >
          @{item.label}
        </div>
      ))}
    </div>
  );
});

MentionList.displayName = 'MentionList';

const normalizeLink = (raw: string) => {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

/* ── SVG icons for toolbar ── */
const icUndo = <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7h7a3 3 0 010 6H8"/><path d="M6 4L3 7l3 3"/></svg>;
const icRedo = <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13 7H6a3 3 0 000 6h2"/><path d="M10 4l3 3-3 3"/></svg>;
const icImage = <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="12" height="12" rx="2"/><circle cx="5.5" cy="5.5" r="1.2"/><path d="M14 10.5l-3.5-3.5L4 14"/></svg>;
const icFile = <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M9 1H4a1 1 0 00-1 1v12a1 1 0 001 1h8a1 1 0 001-1V5L9 1z"/><path d="M9 1v4h4"/></svg>;
const icLink = <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6.5 8.5a3 3 0 004.2.4l1.5-1.5a3 3 0 00-4.2-4.2L7 4.2"/><path d="M9.5 7.5a3 3 0 00-4.2-.4L3.8 8.6a3 3 0 004.2 4.2L9 11.8"/></svg>;

export const TaskRichTextEditor = forwardRef<TaskRichTextEditorHandle, Props>(({
  value, onChange, placeholder, disabled,
  mentionOptions = [], currentUserId, creatorId, assigneeId, watcherIds: propWatcherIds,
  onUpload, onLocalFiles, allowLocalPaste, dirty = true, defaultToolbarHidden = false, onImageClick,
}, ref) => {
  const [mode, setMode] = useState<EditorMode>('wysiwyg');
  const [toolbarVisible, setToolbarVisible] = useState(!defaultToolbarHidden);
  const [mdValue, setMdValue] = useState('');
  const [mdTick, setMdTick] = useState(0);
  const savedJsonRef = useRef<string | null>(null);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const mdTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const dragCounter = useRef(0);
  const handleUploadRef = useRef<(files: FileList | File[] | null, mode: UploadMode) => Promise<void>>(async () => {});
  const allowLocalPasteRef = useRef(allowLocalPaste);
  allowLocalPasteRef.current = allowLocalPaste;
  const onImageClickRef = useRef(onImageClick);
  onImageClickRef.current = onImageClick;
  // Lets handlePaste/handleDrop call into the Tiptap command API after construction
  // (the editor instance isn't in scope when editorProps are evaluated).
  const editorInstanceRef = useRef<ReturnType<typeof useEditor> | null>(null);
  const editorWrapRef = useRef<HTMLDivElement | null>(null);

  const mentionItems = useMemo(() => {
    const watcherSet = new Set(propWatcherIds ?? []);
    const filtered = mentionOptions
      .filter((item) => item.id !== currentUserId)
      .map((item) => ({ ...item }));

    filtered.sort((a, b) => {
      const scoreOf = (m: MentionOption) => {
        if (m.id === creatorId) return 0;
        if (m.id === assigneeId) return 1;
        if (watcherSet.has(m.id)) return 2;
        return 3;
      };
      const diff = scoreOf(a) - scoreOf(b);
      if (diff !== 0) return diff;
      return a.label.localeCompare(b.label, 'ru');
    });

    return filtered;
  }, [mentionOptions, currentUserId, creatorId, assigneeId, propWatcherIds]);

  const editor = useEditor({
    immediatelyRender: false,
    editable: !disabled,
    content: ensureRichText(value),
    extensions: [
      StarterKit.configure({
        heading: false,
      }),
      Underline,
      ImageResize,
      Link.extend({ inclusive: false }).configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
      }),
      Placeholder.configure({
        placeholder: placeholder ?? 'Добавить описание...',
      }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention',
        },
        suggestion: {
          char: '@',
          items: ({ query }: { query: string }) =>
            mentionItems
              .filter((item) => item.label.toLowerCase().includes(query.toLowerCase()))
              .slice(0, 8),
          render: () => {
            let reactRenderer: ReactRenderer | null = null;
            let popup: HTMLDivElement | null = null;

            const reposition = (clientRect?: DOMRect | null) => {
              if (!clientRect || !popup) return;
              popup.style.left = `${clientRect.left}px`;
              popup.style.top = `${clientRect.bottom + 8}px`;
            };

            return {
              onStart: (props: SuggestionProps<MentionOption>) => {
                popup = document.createElement('div');
                popup.style.cssText = suggestionRootStyle;
                document.body.appendChild(popup);
                reactRenderer = new ReactRenderer(MentionList, {
                  props,
                  editor: props.editor,
                });
                popup.appendChild(reactRenderer.element);
                reposition(props.clientRect?.());
              },
              onUpdate: (props: SuggestionProps<MentionOption>) => {
                reactRenderer?.updateProps(props);
                reposition(props.clientRect?.());
              },
              onKeyDown: (props: { event: KeyboardEvent }) => {
                if (props.event.key === 'Escape') {
                  popup?.remove();
                  reactRenderer?.destroy();
                  popup = null;
                  reactRenderer = null;
                  return true;
                }
                return (reactRenderer?.ref as { onKeyDown?: (event: KeyboardEvent) => boolean } | null)?.onKeyDown?.(props.event) ?? false;
              },
              onExit: () => {
                popup?.remove();
                reactRenderer?.destroy();
                popup = null;
                reactRenderer = null;
              },
            };
          },
        },
        renderText: ({ node }) => `@${node.attrs.label ?? 'участник'}`,
      }),
    ],
    editorProps: {
      handlePaste: (view, event) => {
        const files: File[] = [];
        if (event.clipboardData?.files?.length) {
          files.push(...Array.from(event.clipboardData.files));
        } else if (event.clipboardData?.items) {
          for (const item of Array.from(event.clipboardData.items)) {
            if (item.kind === 'file') {
              const f = item.getAsFile();
              if (f) files.push(f);
            }
          }
        }
        if (!files.length) return false;
        // When allowLocalPaste is set, buffer images as base64 (uploaded on save/send)
        if (allowLocalPasteRef.current) {
          const imageFiles = files.filter((f) => f.type.startsWith('image/'));
          if (imageFiles.length) {
            event.preventDefault();
            imageFiles.forEach((file) => {
              const reader = new FileReader();
              reader.onload = () => {
                const src = reader.result as string;
                // Use Tiptap's setImage command — it handles block placement correctly,
                // unlike a raw tr.insert(pos - 1, ...) which lands inside a paragraph
                // (an inline position) and silently no-ops for block image nodes.
                const ed = editorInstanceRef.current;
                if (ed) {
                  insertImage(ed, src, file.name);
                } else {
                  // Fallback: replaceSelectionWith auto-fits block nodes at the current selection
                  const imageNode = view.state.schema.nodes.image?.create({ src, alt: file.name });
                  if (imageNode) {
                    view.dispatch(view.state.tr.replaceSelectionWith(imageNode));
                  }
                }
              };
              reader.readAsDataURL(file);
            });
            return true;
          }
        }
        if (onUpload) {
          event.preventDefault();
          void handleUploadRef.current(files, 'screenshot');
          return true;
        }
        return false;
      },
      handleDrop: (view, event) => {
        // Internal drag (e.g., user is repositioning an existing inline image
        // within the editor): let ProseMirror handle the move natively to
        // avoid re-uploading/re-embedding the same image. ProseMirror exposes
        // an active internal drag via `view.dragging`.
        if (view.dragging) return false;
        const files = Array.from(event.dataTransfer?.files ?? []);
        if (!files.length) return false;
        // When allowLocalPaste is set, buffer dropped images as base64.
        // Some OSes report an empty MIME type for dragged screenshots, so also
        // treat files with an image-looking extension as images (BUGS-28: the
        // 2nd/3rd screenshot in a multi-drop arrived with an empty type and was
        // wrongly handled as a file attachment).
        if (allowLocalPasteRef.current) {
          const isImageFile = (f: File) =>
            f.type.startsWith('image/') ||
            (!f.type && /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(f.name));
          const imageFiles = files.filter(isImageFile);
          if (imageFiles.length) {
            event.preventDefault();
            imageFiles.forEach((file) => {
              const reader = new FileReader();
              reader.onload = () => {
                const src = reader.result as string;
                // See note in handlePaste: setImage correctly inserts a block image.
                const ed = editorInstanceRef.current;
                if (ed) {
                  insertImage(ed, src, file.name);
                } else {
                  const imageNode = view.state.schema.nodes.image?.create({ src, alt: file.name });
                  if (imageNode) {
                    view.dispatch(view.state.tr.replaceSelectionWith(imageNode));
                  }
                }
              };
              reader.readAsDataURL(file);
            });
            // A mixed drop (images + documents): inline the images above and
            // still upload the non-image remainder as attachments so nothing is
            // silently dropped.
            const nonImages = files.filter((f) => !isImageFile(f));
            if (nonImages.length && onUpload) {
              void handleUploadRef.current(nonImages, 'file');
            }
            return true;
          }
        }
        if (onUpload) {
          event.preventDefault();
          const hasImages = files.some((f) => f.type.startsWith('image/'));
          void handleUploadRef.current(files, hasImages ? 'media' : 'file');
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: nextEditor }) => {
      onChange(JSON.stringify(nextEditor.getJSON()));
    },
  });

  // Expose editor to handlePaste/handleDrop closures via ref
  editorInstanceRef.current = editor;

  useEffect(() => {
    if (!editor) return;
    const next = ensureRichText(value);
    const current = JSON.stringify(editor.getJSON());
    if (next !== current) {
      // setContent normally pushes a transaction into history, so the very
      // first "Отменить" click after a task opens would rewind to whatever
      // empty/previous state existed before — sometimes showing leftover
      // content from another task. We dispatch the replacement manually with
      // `addToHistory: false` so the load is invisible to undo.
      try {
        const parsed = JSON.parse(next);
        const newDoc = editor.schema.nodeFromJSON(parsed);
        const tr = editor.state.tr.replaceWith(0, editor.state.doc.content.size, newDoc.content);
        tr.setMeta('addToHistory', false);
        tr.setMeta('preventUpdate', true);
        editor.view.dispatch(tr);
      } catch {
        editor.commands.setContent(JSON.parse(next), { emitUpdate: false });
      }
    }
  }, [editor, value]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  // Detect plain clicks on rendered <img> elements inside the editor and
  // forward them to onImageClick (used as a lightbox opener). We attach a
  // native listener on the wrap so that drag-to-resize handles (which fire
  // mousedown but not click without movement) keep working — a click event
  // only fires when there was no drag.
  useEffect(() => {
    const root = editorWrapRef.current;
    if (!root) return;
    const handler = (e: MouseEvent) => {
      const cb = onImageClickRef.current;
      if (!cb) return;
      const target = e.target as HTMLElement | null;
      if (!target) return;
      // Ignore clicks on the resize handles themselves (they are siblings of
      // the <img> inside the resize wrapper produced by tiptap-extension-resize-image).
      if (target.tagName !== 'IMG') return;
      const src = (target as HTMLImageElement).src;
      if (!src) return;
      // Don't preventDefault — ProseMirror's own listener (attached to the
      // inner contenteditable) has already run by the time this bubbling
      // handler fires, so the image still gets selected and resize handles
      // still appear. We just additionally open the lightbox.
      cb(src);
    };
    root.addEventListener('click', handler);
    return () => root.removeEventListener('click', handler);
  }, [mode]);

  useImperativeHandle(ref, () => ({
    insertImage: (url: string, alt?: string) => {
      if (editor) insertImage(editor, url, alt ?? '');
    },
    insertLink: (url: string, text?: string) => {
      if (!editor) return;
      const normalized = normalizeLink(url);
      if (!normalized) return;
      if (text?.trim()) {
        editor.chain().focus().insertContent({
          type: 'text',
          text: text.trim(),
          marks: [{ type: 'link', attrs: { href: normalized } }],
        }).run();
        return;
      }
      editor.chain().focus().extendMarkRange('link').setLink({ href: normalized }).run();
    },
    focus: () => {
      editor?.chain().focus().run();
    },
    flushEmbeddedImages: async () => {
      // Find all data-URL images in the current value, upload them, and replace with server URLs
      if (!onUpload) return value;
      try {
        const doc = JSON.parse(value);
        const dataImages: { path: number[]; src: string; alt: string }[] = [];
        const walk = (node: JSONContent, path: number[]) => {
          if (node.type === 'image' && typeof node.attrs?.src === 'string' && node.attrs.src.startsWith('data:')) {
            dataImages.push({ path: [...path], src: node.attrs.src, alt: node.attrs.alt ?? '' });
          }
          if (node.content) node.content.forEach((child: JSONContent, i: number) => walk(child, [...path, i]));
        };
        walk(doc, []);
        if (!dataImages.length) return value;

        // Convert data URLs to files and upload
        const files = dataImages.map((img, idx) => {
          const [header, b64] = img.src.split(',');
          const mime = header.match(/data:(.*?);/)?.[1] ?? 'image/png';
          const ext = mime.split('/')[1] ?? 'png';
          const binary = atob(b64);
          const arr = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i);
          return new File([arr], img.alt || `image-${idx}.${ext}`, { type: mime });
        });

        const results = await onUpload(files, 'media');

        // Replace data URLs with server URLs in the doc
        const updated = JSON.parse(JSON.stringify(doc));
        dataImages.forEach((img, idx) => {
          if (!results[idx]) return;
          let node = updated;
          for (const p of img.path) node = node.content[p];
          if (node?.attrs) node.attrs.src = results[idx].url;
        });

        const newValue = JSON.stringify(updated);
        onChange(newValue);
        return newValue;
      } catch {
        return value;
      }
    },
  }));

  const applyLink = () => {
    if (!editor) return;
    const normalized = normalizeLink(linkUrl);
    if (!normalized) return;
    if (linkText.trim()) {
      editor.chain().focus().insertContent({
        type: 'text',
        text: linkText.trim(),
        marks: [{ type: 'link', attrs: { href: normalized } }],
      }).run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: normalized }).run();
    }
    setLinkUrl('');
    setLinkText('');
    setShowLinkForm(false);
  };

  const handleUpload = useCallback(async (files: FileList | File[] | null, uploadMode: UploadMode) => {
    if (!files || !onUpload || !editor) return;
    const inputFiles = Array.isArray(files) ? files : Array.from(files);
    if (!inputFiles.length) return;
    const uploaded = await onUpload(inputFiles, uploadMode);
    uploaded.forEach((item) => {
      const currentEnd = editor.state.doc.content.size;
      if (item.contentType?.startsWith('image/')) {
        insertImage(editor, item.url, item.name, currentEnd);
      } else {
        editor.chain().focus(currentEnd).insertContent({
          type: 'text',
          text: item.name,
          marks: [{ type: 'link', attrs: { href: item.url } }],
        }).run();
      }
    });
  }, [editor, onUpload]);

  useEffect(() => { handleUploadRef.current = handleUpload; }, [handleUpload]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current += 1;
    if (dragCounter.current === 1) setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback((_e: React.DragEvent) => {
    // Only reset the drag-over visual here. The actual file handling is owned by
    // ProseMirror's editorProps.handleDrop (base64-inline for images, onUpload
    // otherwise). Previously this React handler ALSO uploaded the same files,
    // so a multi-image drop was processed twice — the extras came back as file
    // attachments plus filename text nodes (BUGS-28). Do not re-add upload logic
    // here; keep a single drop owner.
    dragCounter.current = 0;
    setDragOver(false);
  }, []);

  const canDoCommand = useCallback((cmd: 'undo' | 'redo') => {
    void mdTick; // re-evaluate button state whenever content changes
    try {
      return document.queryCommandEnabled(cmd);
    } catch {
      return false;
    }
  }, [mdTick]);

  const insertMdSyntax = useCallback((before: string, after: string = '') => {
    const ta = mdTextareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = mdValue.slice(start, end);
    const replacement = `${before}${selected}${after}`;
    const next = mdValue.slice(0, start) + replacement + mdValue.slice(end);
    setMdValue(next);
    onChange(markdownToJson(next));
    requestAnimationFrame(() => {
      ta.focus();
      const cursorPos = selected ? start + replacement.length : start + before.length;
      ta.setSelectionRange(cursorPos, cursorPos);
    });
  }, [mdValue, onChange]);

  const insertMdLine = useCallback((prefix: string) => {
    const ta = mdTextareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const lineStart = mdValue.lastIndexOf('\n', start - 1) + 1;
    const next = mdValue.slice(0, lineStart) + prefix + mdValue.slice(lineStart);
    setMdValue(next);
    onChange(markdownToJson(next));
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(start + prefix.length, start + prefix.length);
    });
  }, [mdValue, onChange]);

  const switchToMarkdown = useCallback(() => {
    savedJsonRef.current = value;
    const md = jsonToMarkdown(value);
    setMdValue(md);
    setMode('markdown');
  }, [value]);

  const switchToWysiwyg = useCallback(() => {
    const originalMd = savedJsonRef.current ? jsonToMarkdown(savedJsonRef.current) : '';
    if (mdValue.trim() === originalMd.trim() && savedJsonRef.current) {
      // Markdown was not edited — restore original JSON exactly (no conversion loss)
      onChange(savedJsonRef.current);
    } else if (mdValue.trim()) {
      const converted = markdownToJson(mdValue);
      try {
        const parsed = JSON.parse(converted);
        const hasContent = parsed?.content?.length > 0 &&
          !(parsed.content.length === 1 && parsed.content[0].type === 'paragraph' && !parsed.content[0].content);
        if (hasContent) {
          onChange(converted);
        } else if (savedJsonRef.current) {
          onChange(savedJsonRef.current);
        }
      } catch {
        if (savedJsonRef.current) onChange(savedJsonRef.current);
      }
    } else if (savedJsonRef.current) {
      onChange(savedJsonRef.current);
    }
    savedJsonRef.current = null;
    setMode('wysiwyg');
  }, [mdValue, onChange]);

  const isMarkActive = useCallback((markName: string) => {
    if (!editor) return false;
    return editor.isActive(markName);
  }, [editor]);

  return (
    <Root>
      <ModeBar>
        <ModeBtn $active={mode === 'wysiwyg'} onClick={() => { if (mode !== 'wysiwyg') switchToWysiwyg(); }}>
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
            <path d="M3 4h10M8 4v9"/>
          </svg> Визуальный
        </ModeBtn>
        <ModeBtn $active={mode === 'markdown'} onClick={() => { if (mode !== 'markdown') switchToMarkdown(); }}>
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
            <path d="M5 5l-2 3 2 3M11 5l2 3-2 3"/>
          </svg> Markdown
        </ModeBtn>
        <span style={{ flex: 1 }} />
        <ModeBtn
          $active={toolbarVisible}
          onClick={() => setToolbarVisible((v) => !v)}
          title={toolbarVisible ? 'Скрыть форматирование' : 'Показать форматирование'}
        >
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
            <path d="M3 4h10M3 8h7M3 12h10"/>
          </svg> Форматирование {toolbarVisible ? '▴' : '▾'}
        </ModeBtn>
      </ModeBar>

      {toolbarVisible && (
      <Toolbar>
        {mode === 'wysiwyg' ? (
          <>
            <Tooltip content="Отменить (Ctrl+Z)" delay={300}>
              <ToolbarBtn type="button" onClick={() => editor?.chain().focus().undo().run()} disabled={disabled || !dirty || !editor?.can().undo()}>
                {icUndo}
              </ToolbarBtn>
            </Tooltip>
            <Tooltip content="Повторить (Ctrl+Shift+Z)" delay={300}>
              <ToolbarBtn type="button" onClick={() => editor?.chain().focus().redo().run()} disabled={disabled || !dirty || !editor?.can().redo()}>
                {icRedo}
              </ToolbarBtn>
            </Tooltip>
            <Divider />
            <Tooltip content="Жирный" delay={300}><ToolbarBtn type="button" $active={isMarkActive('bold')} onClick={() => editor?.chain().focus().toggleBold().run()} disabled={disabled}><b>B</b></ToolbarBtn></Tooltip>
            <Tooltip content="Курсив" delay={300}><ToolbarBtn type="button" $active={isMarkActive('italic')} onClick={() => editor?.chain().focus().toggleItalic().run()} disabled={disabled}><i>I</i></ToolbarBtn></Tooltip>
            <Tooltip content="Зачёркнутый" delay={300}><ToolbarBtn type="button" $active={isMarkActive('strike')} onClick={() => editor?.chain().focus().toggleStrike().run()} disabled={disabled}><s>S</s></ToolbarBtn></Tooltip>
            <Tooltip content="Подчёркнутый" delay={300}><ToolbarBtn type="button" $active={isMarkActive('underline')} onClick={() => editor?.chain().focus().toggleUnderline().run()} disabled={disabled}><u>U</u></ToolbarBtn></Tooltip>
            <Tooltip content="Код" delay={300}><ToolbarBtn type="button" $active={isMarkActive('code')} onClick={() => editor?.chain().focus().toggleCode().run()} disabled={disabled}>code</ToolbarBtn></Tooltip>
            <Tooltip content="Блок кода" delay={300}><ToolbarBtn type="button" $active={editor?.isActive('codeBlock')} onClick={() => editor?.chain().focus().toggleCodeBlock().run()} disabled={disabled}>{'{ }'}</ToolbarBtn></Tooltip>
            <Tooltip content="Маркированный список" delay={300}><ToolbarBtn type="button" $active={editor?.isActive('bulletList')} onClick={() => editor?.chain().focus().toggleBulletList().run()} disabled={disabled}>list</ToolbarBtn></Tooltip>
            <Tooltip content="Нумерованный список" delay={300}><ToolbarBtn type="button" $active={editor?.isActive('orderedList')} onClick={() => editor?.chain().focus().toggleOrderedList().run()} disabled={disabled}>1.</ToolbarBtn></Tooltip>
            <Divider />
            <Tooltip content="Вставить ссылку" delay={300}>
              <ToolbarBtn type="button" $active={editor?.isActive('link') || showLinkForm} onClick={() => setShowLinkForm((current) => !current)} disabled={disabled}>
                {icLink}
              </ToolbarBtn>
            </Tooltip>
            <Tooltip content="Вставить изображение" delay={300}>
              <ToolbarBtn type="button" onClick={() => {
                // When upload is supported OR local paste buffering is enabled, open file picker
                if (onUpload || allowLocalPasteRef.current) { imageInputRef.current?.click(); return; }
                const url = window.prompt('URL изображения');
                if (url && url.trim() && editor) insertImage(editor, url.trim(), '');
              }} disabled={disabled}>
                {icImage}
              </ToolbarBtn>
            </Tooltip>
            {(onUpload || onLocalFiles) ? (
              <Tooltip content="Прикрепить файл" delay={300}>
                <ToolbarBtn type="button" onClick={() => fileInputRef.current?.click()} disabled={disabled}>
                  {icFile}
                </ToolbarBtn>
              </Tooltip>
            ) : null}
          </>
        ) : (
          <>
            <Tooltip content="Отменить (Ctrl+Z)" delay={300}>
              <ToolbarBtn type="button" onClick={() => document.execCommand('undo')} disabled={disabled || !canDoCommand('undo')}>
                {icUndo}
              </ToolbarBtn>
            </Tooltip>
            <Tooltip content="Повторить (Ctrl+Shift+Z)" delay={300}>
              <ToolbarBtn type="button" onClick={() => document.execCommand('redo')} disabled={disabled || !canDoCommand('redo')}>
                {icRedo}
              </ToolbarBtn>
            </Tooltip>
            <Divider />
            <Tooltip content="Жирный **text**" delay={300}><ToolbarBtn type="button" onClick={() => insertMdSyntax('**', '**')} disabled={disabled}><b>B</b></ToolbarBtn></Tooltip>
            <Tooltip content="Курсив *text*" delay={300}><ToolbarBtn type="button" onClick={() => insertMdSyntax('*', '*')} disabled={disabled}><i>I</i></ToolbarBtn></Tooltip>
            <Tooltip content="Зачёркнутый ~~text~~" delay={300}><ToolbarBtn type="button" onClick={() => insertMdSyntax('~~', '~~')} disabled={disabled}><s>S</s></ToolbarBtn></Tooltip>
            <Tooltip content="Подчёркнутый <u>text</u>" delay={300}><ToolbarBtn type="button" onClick={() => insertMdSyntax('<u>', '</u>')} disabled={disabled}><u>U</u></ToolbarBtn></Tooltip>
            <Tooltip content="Код `code`" delay={300}><ToolbarBtn type="button" onClick={() => insertMdSyntax('`', '`')} disabled={disabled}>code</ToolbarBtn></Tooltip>
            <Tooltip content="Блок кода ```" delay={300}><ToolbarBtn type="button" onClick={() => insertMdSyntax('\n```\n', '\n```\n')} disabled={disabled}>{'{ }'}</ToolbarBtn></Tooltip>
            <Tooltip content="Маркированный список" delay={300}><ToolbarBtn type="button" onClick={() => insertMdLine('- ')} disabled={disabled}>list</ToolbarBtn></Tooltip>
            <Tooltip content="Нумерованный список" delay={300}><ToolbarBtn type="button" onClick={() => insertMdLine('1. ')} disabled={disabled}>1.</ToolbarBtn></Tooltip>
            <Divider />
            <Tooltip content="Ссылка [text](url)" delay={300}><ToolbarBtn type="button" onClick={() => insertMdSyntax('[', '](url)')} disabled={disabled}>{icLink}</ToolbarBtn></Tooltip>
            <Tooltip content="Изображение ![alt](url)" delay={300}><ToolbarBtn type="button" onClick={() => insertMdSyntax('![', '](url)')} disabled={disabled}>{icImage}</ToolbarBtn></Tooltip>
            {(onUpload || onLocalFiles) ? (
              <Tooltip content="Прикрепить файл" delay={300}>
                <ToolbarBtn type="button" onClick={() => fileInputRef.current?.click()} disabled={disabled}>
                  {icFile}
                </ToolbarBtn>
              </Tooltip>
            ) : null}
          </>
        )}
      </Toolbar>
      )}

      {mode === 'wysiwyg' && showLinkForm ? (
        <LinkRow>
          <LinkInput value={linkUrl} onChange={(event) => setLinkUrl(event.target.value)} placeholder="https://example.com" />
          <LinkInput value={linkText} onChange={(event) => setLinkText(event.target.value)} placeholder="Текст ссылки" />
          <LinkAction type="button" onClick={applyLink} disabled={!linkUrl.trim()}>
            Вставить
          </LinkAction>
        </LinkRow>
      ) : null}

      {mode === 'wysiwyg' ? (
        <EditorWrap
          ref={editorWrapRef}
          $dragOver={dragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <EditorContent editor={editor} />
        </EditorWrap>
      ) : (
        <MdTextarea
          ref={mdTextareaRef}
          value={mdValue}
          onChange={(e) => {
            setMdValue(e.target.value);
            setMdTick((t) => t + 1);
            const json = markdownToJson(e.target.value);
            onChange(json);
          }}
          onInput={() => {
            setMdTick((t) => t + 1);
          }}
          onKeyUp={() => {
            setMdTick((t) => t + 1);
          }}
          onFocus={() => {
            setMdTick((t) => t + 1);
          }}
          placeholder={placeholder ?? 'Введите текст в формате Markdown...'}
          disabled={disabled}
        />
      )}

      <input
        ref={imageInputRef}
        type="file"
        hidden
        multiple
        accept="image/*"
        onChange={(event) => {
          const files = event.target.files;
          // No upload handler but local-paste buffering is enabled: embed as data URLs
          // (flushEmbeddedImages will upload them on save).
          if (!onUpload && allowLocalPasteRef.current && files && files.length) {
            Array.from(files).forEach((file) => {
              if (!file.type.startsWith('image/')) return;
              const reader = new FileReader();
              reader.onload = () => {
                const src = reader.result as string;
                const ed = editorInstanceRef.current;
                if (ed) {
                  insertImage(ed, src, file.name);
                }
              };
              reader.readAsDataURL(file);
            });
          } else {
            void handleUpload(files, 'media');
          }
          event.target.value = '';
        }}
      />
      <input
        ref={fileInputRef}
        type="file"
        hidden
        multiple
        onChange={(event) => {
          const files = event.target.files;
          if (!onUpload && onLocalFiles && files && files.length) {
            // No card id yet (create form): buffer the files; the parent uploads them on save.
            onLocalFiles(Array.from(files));
          } else {
            void handleUpload(files, 'file');
          }
          event.target.value = '';
        }}
      />
    </Root>
  );
});

TaskRichTextEditor.displayName = 'TaskRichTextEditor';

/* ── Lightweight read-only rich text renderer ── */

const RichTextViewWrap = styled.div`
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.55;

  p { margin: 0 0 6px; &:last-child { margin-bottom: 0; } }
  ul, ol { padding-left: 18px; margin: 0 0 6px; }
  pre { padding: 8px 10px; border-radius: 6px; background: #111827; color: #e5e7eb; overflow-x: auto; font-size: 12px; margin: 0 0 6px; }
  code { padding: 1px 3px; border-radius: 3px; background: color-mix(in srgb, var(--bg-hover) 85%, transparent); font-size: 12px; }
  pre code { padding: 0; background: transparent; }
  a { color: var(--accent); text-decoration: underline; cursor: pointer; }
  img { max-width: 100%; border-radius: 8px; display: block; margin: 4px 0; cursor: pointer; &:hover { opacity: 0.85; } }
  .mention { display: inline-flex; align-items: center; gap: 3px; padding: 1px 5px; border-radius: 999px; background: var(--accent-muted); color: var(--accent); font-weight: 600; font-size: 12px; }
`;

const autolinkHtml = (html: string): string =>
  html.replace(
    /(?<!")(https?:\/\/[^\s<>"]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>',
  );

const makeLinksOpenable = (html: string): string =>
  html.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ');

export const RichTextView: React.FC<{ content: string; className?: string; onImageClick?: (src: string) => void }> = ({ content, className, onImageClick }) => {
  const html = useMemo(() => {
    try {
      const parsed = JSON.parse(content);
      const raw = generateHTML(parsed, tiptapExtensionsForHtml);
      return makeLinksOpenable(autolinkHtml(raw));
    } catch {
      return '';
    }
  }, [content]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'IMG' && onImageClick) {
      e.preventDefault();
      onImageClick((target as HTMLImageElement).src);
    }
  }, [onImageClick]);

  if (!html) return null;
  return (
    <RichTextViewWrap
      className={className}
      dangerouslySetInnerHTML={{ __html: html }}
      onClick={handleClick}
      style={onImageClick ? { cursor: undefined } : undefined}
    />
  );
};
